/**
 * WASM Loader for AgentShield in Edge Runtime
 * Handles loading and initialization of WebAssembly module
 *
 * IMPORTANT: wasm-bindgen Import Names Limitation
 * ================================================
 * The import function names (e.g., __wbg_log_1d3ae13c3d5e6b8e) are generated
 * by wasm-bindgen with hash suffixes that change with each WASM rebuild.
 * If the WASM module is rebuilt, these function names MUST be updated to match
 * the new exports from wasm-pack.
 *
 * To get the correct function names after a WASM rebuild:
 * 1. Run `wasm-pack build --target web` in the rust/crates/agentshield-wasm directory
 * 2. Check the generated `agentshield_wasm_bg.js` file for the actual export names
 * 3. Update the import object in this file to match
 *
 * TODO: Consider using the generated JS bindings from wasm-pack instead of
 * manually specifying imports, or add CI verification to catch mismatches.
 */

// Dynamic WASM loading - no direct imports to avoid build issues
let wasmInstance: WebAssembly.Instance | null = null;
interface WasmExports {
  detect_agent: (ptr: number, len: number) => number;
  get_version: () => number;
  __wbindgen_malloc: (size: number) => number;
  __wbindgen_free: (ptr: number, size: number) => void;
  __wbindgen_realloc: (ptr: number, oldSize: number, newSize: number) => number;
  memory: WebAssembly.Memory;
  [key: string]: unknown;
}
let wasmExports: WasmExports | null = null;
let initPromise: Promise<void> | null = null;

// Store the WASM module URL for runtime loading
const WASM_PATH = '/wasm/agentshield_wasm_bg.wasm';

// Store base URL for constructing absolute URLs in Edge Runtime
let baseUrl: string | null = null;

/**
 * Set the base URL for WASM loading (needed in Edge Runtime)
 */
export function setWasmBaseUrl(url: string) {
  baseUrl = url;
}

/**
 * Construct the full WASM URL based on context
 */
function getWasmUrl(): string {
  // If we have a base URL (Edge Runtime), construct absolute URL
  if (baseUrl) {
    try {
      const url = new URL(baseUrl);
      // Use the origin to construct the full URL
      return `${url.origin}${WASM_PATH}`;
    } catch {
      // Fallback to relative path if URL construction fails
      return WASM_PATH;
    }
  }
  // Default to relative path
  return WASM_PATH;
}

/**
 * Initialize the WASM module
 * This is safe to call multiple times - it will only initialize once
 */
export async function initWasm(): Promise<boolean> {
  if (wasmExports) return true;
  if (initPromise) {
    await initPromise;
    return !!wasmExports;
  }

  initPromise = (async () => {
    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      try {
        const wasmUrl = getWasmUrl();

        // Try streaming compilation first (most efficient)
        if (typeof WebAssembly.instantiateStreaming === 'function') {
          try {
            const response = await fetch(wasmUrl, { signal: controller.signal });
            clearTimeout(timeout);

            if (!response.ok) {
              throw new Error(`Failed to fetch WASM: ${response.status}`);
            }

            // Clone response for streaming (in case we need to retry)
            const streamResponse = response.clone();

            // Use streaming compilation for best performance
            const { instance } = await WebAssembly.instantiateStreaming(streamResponse, {
              wbg: {
                __wbg_log_1d3ae13c3d5e6b8e: (ptr: number, len: number) => {
                  if (process.env.NODE_ENV !== 'production') {
                    console.debug('WASM:', ptr, len);
                  }
                },
                __wbindgen_throw: (ptr: number, len: number) => {
                  throw new Error(`WASM Error at ${ptr}, length ${len}`);
                },
              },
            });

            wasmInstance = instance;
            wasmExports = instance.exports as WasmExports;

            if (process.env.NODE_ENV !== 'production') {
              console.debug('[AgentShield] ✅ WASM module initialized with streaming');
            }
            return;
          } catch (streamError) {
            // If not aborted, try fallback
            if (!controller.signal.aborted) {
              if (process.env.NODE_ENV !== 'production') {
                console.debug(
                  '[AgentShield] Streaming compilation failed, falling back to standard compilation'
                );
              }
            } else {
              throw streamError;
            }
          }
        }

        // Fallback to standard compilation if streaming not available
        const response = await fetch(wasmUrl, { signal: controller.signal });
        clearTimeout(timeout);

        if (!response.ok) {
          throw new Error(`Failed to fetch WASM: ${response.status}`);
        }

        const wasmArrayBuffer = await response.arrayBuffer();
        const compiledModule = await WebAssembly.compile(wasmArrayBuffer);

        const imports = {
          wbg: {
            __wbg_log_1d3ae13c3d5e6b8e: (ptr: number, len: number) => {
              if (process.env.NODE_ENV !== 'production') {
                console.debug('WASM:', ptr, len);
              }
            },
            __wbindgen_throw: (ptr: number, len: number) => {
              throw new Error(`WASM Error at ${ptr}, length ${len}`);
            },
          },
        };

        wasmInstance = await WebAssembly.instantiate(compiledModule, imports);
        wasmExports = wasmInstance.exports as WasmExports;

        if (process.env.NODE_ENV !== 'production') {
          console.debug('[AgentShield] ✅ WASM module initialized via fallback');
        }
      } catch (fetchError: unknown) {
        const error = fetchError as { name?: string; message?: string };
        if (error.name === 'AbortError') {
          console.warn(
            '[AgentShield] WASM fetch timed out after 3 seconds - using pattern detection'
          );
        } else {
          console.warn(
            '[AgentShield] Failed to fetch WASM file:',
            error.message || 'Unknown error'
          );
        }
        wasmExports = null;
      }
    } catch (error) {
      console.error('[AgentShield] Failed to initialize WASM:', error);
      wasmExports = null;
    }
  })();

  await initPromise;
  return !!wasmExports;
}

/**
 * Detect agent using WASM
 * Returns null if WASM is not available
 *
 * NOTE: This function currently returns null to gracefully fall back to pattern detection.
 * The raw WASM instantiation approach in this file is incompatible with wasm-bindgen's
 * generated bindings which require complex JS glue code for memory management and
 * string marshalling. For proper WASM detection, use edge-wasm-middleware.ts which
 * correctly handles the wasm-bindgen API with JsRequestMetadata creation.
 *
 * TODO: Rewrite this to use the high-level wasm-bindgen JS bindings or port the
 * edge-wasm-middleware.ts pattern here.
 */
export async function detectAgentWithWasm(
  _userAgent: string | undefined,
  _headers: Record<string, string>,
  _ipAddress?: string
): Promise<{
  isAgent: boolean;
  confidence: number;
  agent?: string;
  verificationMethod: string;
  riskLevel: string;
} | null> {
  // Return null to fall back to pattern detection
  // The raw WASM API used in this file doesn't match wasm-bindgen's generated exports
  // which require JsRequestMetadata objects created via jsrequestmetadata_new()
  return null;
}

/**
 * Get WASM version
 */
export async function getWasmVersion(): Promise<string | null> {
  const initialized = await initWasm();
  if (!initialized || !wasmExports) {
    return null;
  }

  if (typeof wasmExports.version === 'function') {
    return wasmExports.version();
  }

  return 'unknown';
}

/**
 * Check if WASM is available and working
 */
export async function isWasmAvailable(): Promise<boolean> {
  try {
    const initialized = await initWasm();
    if (!initialized) return false;

    // Try to get version as a simple test
    const version = await getWasmVersion();
    return version !== null;
  } catch {
    return false;
  }
}
